/**
 * Converts tag_value / rfid_tag to the Guarita serial used for matching against a Nice
 * MG3000's raw registration list.
 *
 * Wiegand 26 "facility,card" (e.g. "157,44941") → (facility << 16) | card = 10334093.
 * A real Wiegand26 facility code is only 8 bits (0-255) — a value outside that range means
 * the string isn't actually a Wiegand26 tag, most likely a ControlID 40-bit UHF tag's raw
 * "facility,card" string reused as-is (tag_value_dec stores that 40-bit form — facility << 32
 * | card — do NOT use it for Guarita, and don't reuse tag_value's comma-split for it either).
 * Blindly combining an oversized facility produces an inflated value that can never be a real
 * Guarita serial and which the device correctly rejects (production incident: "809,59905" →
 * 53078529, which exceeds even the widest 28-bit range this hardware supports for any device
 * type) — so this returns '' instead, letting callers skip/report the tag as unsyncable
 * rather than sending a doomed registration request.
 *
 * Non-Wiegand values are resolved by utils/tagValueRadix, so hex (0x prefix, A-F digits, or
 * a leading zero) and decimal mean the same thing here as they do on every other sync path.
 *
 * The Nice MG3000 only reports a plain 3-byte (24-bit) serial for CT/TA/TP/BM/SN device
 * types, but RF/"Controle" (remote control) registrations get a 4th nibble packed into the
 * byte otherwise used for `disp_dest`, giving those a real 28-bit range (see iot-connect's
 * management.go parseDeviceRecord/CardDecimalToSerial). Masking to 24 bits unconditionally —
 * as this used to do — silently drops that nibble and collapses two DIFFERENT people's tags
 * onto the same computed value whenever their low 24 bits happen to match (e.g. 170715649
 * and 2943489), making the UI display one person's card under another person's name. 28-bit
 * is safe for every case: values that already fit in 24 bits are untouched by the wider
 * mask, and 28-bit is still the hardware's real ceiling for every device type this
 * integration uses, so no value can spuriously "un-collide" only to collide with a third tag
 * at the wider width.
 */
import { tagValueToDecimal } from './tagValueRadix';

export function guaritaCardDecimal(tagValue: string): string {
  const v = (tagValue || '').trim();
  if (v.includes(',')) {
    const [f, c] = v.split(',');
    const facility = parseInt(f.trim(), 10);
    const card = parseInt(c.trim(), 10);
    if (!isNaN(facility) && !isNaN(card)) {
      if (facility < 0 || facility > 0xFF) return '';
      return String(((facility << 16) | card) & 0xFFFFFFF);
    }
  }
  // A leading zero (like an explicit 0x, or an A-F digit) marks the value as hex — the
  // reader's own padding, and the rule deviceCommunication.parseRfidTagToCardValue and the
  // tag entry forms both use (see utils/tagValueRadix). Reading "0989390" as decimal 989390
  // here — and then returning the raw string, which matches no device serial at all — made
  // a hex tag simply unsyncable to Guarita while ControlID synced it correctly.
  const n = tagValueToDecimal(v, 'dec');
  if (n === null) return v;
  return String(n & 0xFFFFFFF);
}
